import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./createOrder.scss";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  updateDoc,
  query,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase";
import {
  FaTrash,
  FaCheckCircle,
  FaPrint,
  FaWhatsapp,
  FaImage,
  FaPlus,
} from "react-icons/fa";

// ==========================================
// UTILITIES
// ==========================================
const formatOrderIdPrefix = (date) => {
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  return `SV${year}${month}`;
};

const generateOrderId = async (currentDate) => {
  const monthPrefix = formatOrderIdPrefix(currentDate);
  const currentDay = currentDate.getDate().toString().padStart(2, "0");
  const snapshot = await getDocs(query(collection(db, "partyOrders")));
  let highestSequence = 0;
  snapshot.docs.forEach((doc) => {
    const orderId = doc.data().orderId;
    if (orderId && orderId.startsWith(monthPrefix)) {
      const sequence = parseInt(orderId.substring(8), 10);
      if (!isNaN(sequence) && sequence > highestSequence)
        highestSequence = sequence;
    }
  });
  return `${monthPrefix}${currentDay}${(highestSequence + 1).toString().padStart(3, "0")}`;
};

// NEW: Extractor for ALL images associated with an item
const extractAllItemImages = (item) => {
  if (
    item?.fileUrls &&
    Array.isArray(item.fileUrls) &&
    item.fileUrls.length > 0
  ) {
    return item.fileUrls;
  }
  if (item?.fileUrl) {
    return [item.fileUrl];
  }
  return [];
};

const getBulkItemDetailString = (item, order) => {
  if (item.beadSize)
    return `${order.subType || "Bulk"} - ${item.beadSize} / ${item.length}`;
  if (item.weightRange)
    return `${item.type || order.subType || "Watti Mani"} - ${item.weightRange}`;
  return item.productName || order.subType || "Bulk Item";
};

const triggerPrintJobCard = (order, partyName) => {
  const printWindow = window.open("", "", "height=800,width=800");
  let allImages = [];

  if (order.files) allImages.push(...order.files);
  if (order.singleItems) {
    order.singleItems.forEach((item) => {
      allImages.push(...extractAllItemImages(item));
    });
  } else if (order.items) {
    order.items.forEach((item) => {
      allImages.push(...extractAllItemImages(item));
    });
  }

  // De-duplicate array
  allImages = [...new Set(allImages)];

  const printImages = allImages.slice(0, 4); // Keep limited to 4 for print layout safety
  const imgCountClass = `img-count-${printImages.length}`;

  const htmlContent = `
    <html><head><title>Job Card - ${order.orderId}</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; padding: 0; }
      .job-work-card { width: 210mm; margin: 0 auto; background: white; padding: 20px; }
      .card-header-print { text-align: center; border-bottom: 3px double #000; padding-bottom: 10px; margin-bottom: 15px; }
      .card-header-print h1 { font-size: 1.6em; color: #1e293b; margin: 0; }
      .order-id-print { font-size: 1.1em; font-weight: bold; color: #dc2626; }
      .print-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 10px; }
      .section-data { border: 1px solid #94a3b8; border-radius: 4px; padding: 10px; margin-bottom: 10px; }
      .section-data h2 { font-size: 1em; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; margin-top: 0; margin-bottom: 8px; }
      .data-row-print { display: flex; justify-content: space-between; padding: 3px 0; font-size: 0.9em; }
      .label-print { font-weight: bold; color: #334155; }
      .item-details-table { width: 100%; border-collapse: collapse; font-size: 0.85em; margin-bottom: 10px; }
      .item-details-table th, .item-details-table td { border: 1px solid #94a3b8; padding: 6px; text-align: left; }
      .item-details-table th { background-color: #f1f5f9; }
      .print-images-container { display: grid; gap: 10px; width: 100%; height: 400px; justify-content: center; }
      .img-count-1 { grid-template-columns: 1fr; } .img-count-2 { grid-template-columns: 1fr 1fr; } .img-count-3, .img-count-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
      .print-image-wrapper { width: 100%; height: 100%; border: 1px solid #cbd5e1; display: flex; justify-content: center; align-items: center; overflow: hidden; }
      .job-work-image { width: 100%; height: 100%; object-fit: contain; }
      @media print { @page { size: A4; margin: 10mm; } body { -webkit-print-color-adjust: exact; } }
    </style></head><body>
    <div class="job-work-card">
      <div class="card-header-print"><h1>JOB WORK CARD</h1><div class="order-id-print">ORDER #${order.orderId}</div></div>
      <div class="print-grid-2">
        <div class="section-data"><h2>Order Info</h2>
          <div class="data-row-print"><span class="label-print">Party:</span><span>${partyName}</span></div>
          <div class="data-row-print"><span class="label-print">Delivery:</span><span>${order.deliveryDate}</span></div>
        </div>
        <div class="section-data"><h2>Type Summary</h2>
          <div class="data-row-print"><span class="label-print">Category:</span><span>${order.orderType}</span></div>
          ${order.orderType === "Single" ? `<div class="data-row-print"><span class="label-print">Product:</span><span>${order.product}</span></div>` : ""}
          ${order.orderType === "Bulk" ? `<div class="data-row-print"><span class="label-print">Sub-Type:</span><span>${order.subType}</span></div>` : ""}
          ${order.orderType === "Casting" ? `<div class="data-row-print"><span class="label-print">Item:</span><span>${order.castingDetails?.item}</span></div>` : ""}
        </div>
      </div>
      <div class="section-data"><h2>Production Details</h2>
      ${
        order.orderType === "Single"
          ? `
        <table class="item-details-table">
          <thead><tr><th>Sr</th><th>Length</th><th>Weight Breakdown</th><th>Qty</th></tr></thead>
          <tbody>
            ${order.singleItems
              .map(
                (row, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td><strong>Main:</strong> ${row.length} ${row.lengthUnit} ${row.pendantLength ? `<br/><strong>Pendant:</strong> ${row.pendantLength} ${row.pendantLengthUnit}` : ""}</td>
                <td>
                  <div><strong>Main Wt:</strong> ${row.weight}</div>
                  ${row.pendantWeight ? `<div><strong>Pendant Wt:</strong> ${row.pendantWeight}</div><div><strong>Total:</strong> ${Number(row.weight) + Number(row.pendantWeight)}</div>` : ""}
                </td>
                <td style="font-weight: bold; text-align: center;">${row.quantity}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `
          : order.orderType === "Bulk"
            ? `
        <table class="item-details-table">
          <thead><tr><th>Sr</th><th>Product Details</th><th>Weight/Specs</th><th>Qty</th></tr></thead>
          <tbody>
            ${order.items
              .map(
                (row, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td><strong>${getBulkItemDetailString(row, order)}</strong></td>
                <td>${row.weight || row.grossWeight || "-"}</td>
                <td style="font-weight: bold; text-align: center;">${row.quantity || row.qty || 1}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `
            : `
        <table class="item-details-table">
          <tbody>
            <tr><th>Size</th><td>${order.castingDetails?.size || "N/A"} ${order.castingDetails?.sizeUnit || ""}</td></tr>
            <tr><th>Carat</th><td>${order.castingDetails?.carat}</td></tr>
            <tr><th>Est Weight</th><td>${order.castingDetails?.weight}</td></tr>
            <tr><th>Stone</th><td>${order.castingDetails?.stone}</td></tr>
            <tr><th>Budget</th><td>${order.castingDetails?.budget || "N/A"}</td></tr>
          </tbody>
        </table>
      `
      }
      </div>
      ${
        printImages.length > 0
          ? `
      <div class="section-data"><h2>Design References</h2>
        <div class="print-images-container ${imgCountClass}">
          ${printImages.map((url) => `<div class="print-image-wrapper"><img src="${url}" class="job-work-image" /></div>`).join("")}
        </div>
      </div>`
          : ""
      }
      <div class="section-data"><h2>Instructions</h2><p>${order.comments || "None"}</p></div>
    </div>
    </body></html>`;
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 800);
};

const openWhatsApp = (order, partyName) => {
  let text = `*✨ NEW ORDER ALERT ✨*\n\n*Order ID:* #${order.orderId}\n*Party:* ${partyName}\n*Delivery Date:* ${order.deliveryDate}\n*Category:* ${order.orderType}\n\n`;
  if (order.orderType === "Single") {
    text += `*Product:* ${order.product}\n\n`;
    order.singleItems.forEach((item, idx) => {
      text += `_Item ${idx + 1}_\n⚖️ Main: ${item.weight} | 📏 Len: ${item.length} ${item.lengthUnit}\n`;
      if (item.pendantWeight)
        text += `⚖️ Pend: ${item.pendantWeight} | 📏 Size: ${item.pendantLength}\n💰 *Total Wt:* ${Number(item.weight) + Number(item.pendantWeight)}\n`;
      text += `📦 Qty: ${item.quantity}\n\n`;
    });
  } else if (order.orderType === "Bulk") {
    text += `*Sub-Type:* ${order.subType}\n\n`;
    order.items.forEach((item) => {
      text += `- ${item.beadSize ? `${item.beadSize} - ${item.length}` : item.weightRange}: *${item.quantity || item.qty} pcs*\n`;
    });
  } else if (order.orderType === "Casting") {
    text += `*Item:* ${order.castingDetails.item}\n*Size:* ${order.castingDetails.size} ${order.castingDetails.sizeUnit}\n*Carat:* ${order.castingDetails.carat}\n*Est Wt:* ${order.castingDetails.weight}\n*Stone:* ${order.castingDetails.stone}\n`;
  }
  if (order.comments) text += `\n*Instructions:* ${order.comments}\n`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
};

// ==========================================
// PORTAL MODALS (Party & Product)
// ==========================================
const AddPartyModal = ({ onClose, onSuccess }) => {
  const [partyName, setPartyName] = useState("");
  const handleSave = async (e) => {
    e.preventDefault();
    if (!partyName.trim()) return;
    try {
      const docRef = await addDoc(collection(db, "parties"), {
        name: partyName.trim(),
      });
      onSuccess({ id: docRef.id, name: partyName.trim() });
      onClose();
    } catch (e) {
      alert("Failed to add party");
    }
  };
  return createPortal(
    <div className="erp-modal-backdrop erp-fade-in">
      <form className="erp-modal-card erp-pop-in" onSubmit={handleSave}>
        <h3>👤 Add Master Party</h3>
        <div className="erp-input-group">
          <input
            type="text"
            autoFocus
            placeholder="e.g. Ranka Jewellers"
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
            required
          />
        </div>
        <div className="erp-modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Party
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

const AddProductModal = ({ onClose, onSuccess }) => {
  const [productName, setProductName] = useState("");
  const handleSave = (e) => {
    e.preventDefault();
    if (productName.trim()) {
      onSuccess(productName.trim());
      onClose();
    }
  };
  return createPortal(
    <div className="erp-modal-backdrop erp-fade-in">
      <form className="erp-modal-card erp-pop-in" onSubmit={handleSave}>
        <h3 style={{ color: "#3b82f6" }}>✨ Add Product Class</h3>
        <div className="erp-input-group">
          <input
            type="text"
            autoFocus
            placeholder="e.g. Diamond Set"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            required
          />
        </div>
        <div className="erp-modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Add Product
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const CreateOrder = () => {
  const [parties, setParties] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState("");
  const [orderType, setOrderType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);

  const [isPartyModalOpen, setIsPartyModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [products, setProducts] = useState([
    "Mangalsutra",
    "Necklace",
    "Ring",
    "Pendant",
    "Mangalsutra + Pendant",
    "Bangle",
  ]);
  const [form, setForm] = useState({
    product: products[0],
    deliveryDate: "",
    comments: "",
  });

  // Order Data States
  const [rows, setRows] = useState([
    {
      id: Date.now(),
      rawFiles: [],
      previewUrls: [],
      length: "",
      lengthUnit: "Inches",
      weight: "",
      pendantLength: "",
      pendantLengthUnit: "Inches",
      pendantWeight: "",
      quantity: "1",
    },
  ]);
  const BEAD_SIZES = [
    "7-0",
    "8-0",
    "9-0",
    "10-0",
    "11-0",
    "12-0",
    "13-0",
    "Kankari500",
    "Kankari300",
  ];
  const LENGTHS = [15, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36];
  const [subType, setSubType] = useState("Mangalsutra");
  const [matrix, setMatrix] = useState(() => {
    let init = {};
    BEAD_SIZES.forEach((s) => {
      init[s] = {};
      LENGTHS.forEach((l) => (init[s][l] = ""));
    });
    return init;
  });
  const [wattiItems, setWattiItems] = useState([]);
  const [wItem, setWItem] = useState({
    fromGrams: "",
    toGrams: "",
    quantity: "",
  });

  const [castingDetails, setCastingDetails] = useState({
    item: "Ring",
    customItem: "",
    carat: "22K",
    customCarat: "",
    weight: "",
    size: "",
    sizeUnit: "Ring Size",
    stone: "Diamond",
    customStone: "",
    budget: "",
  });

  const [globalFiles, setGlobalFiles] = useState({
    rawFiles: [],
    previewUrls: [],
  });

  useEffect(() => {
    const fetchParties = async () => {
      const snap = await getDocs(query(collection(db, "parties")));
      setParties(
        snap.docs.map((doc) => ({ id: doc.id, name: doc.data().name })),
      );
    };
    fetchParties();
  }, []);

  // --- KEYBOARD SHORTCUTS ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        const submitBtn = document.getElementById("main-submit-btn");
        if (submitBtn) submitBtn.click();
      }
      if (e.altKey && e.key === "a" && orderType === "Single") {
        e.preventDefault();
        setRows((r) => [
          ...r,
          {
            id: Date.now(),
            rawFiles: [],
            previewUrls: [],
            length: "",
            lengthUnit: "Inches",
            weight: "",
            pendantLength: "",
            pendantLengthUnit: "Inches",
            pendantWeight: "",
            quantity: "1",
          },
        ]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [orderType]);

  const handleGlobalFiles = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const urls = files.map((f) => URL.createObjectURL(f));
    setGlobalFiles((prev) => ({
      rawFiles: [...prev.rawFiles, ...files],
      previewUrls: [...prev.previewUrls, ...urls],
    }));
  };

  const removeGlobalImage = (idx) => {
    setGlobalFiles((prev) => {
      const raw = [...prev.rawFiles];
      raw.splice(idx, 1);
      const urls = [...prev.previewUrls];
      urls.splice(idx, 1);
      return { rawFiles: raw, previewUrls: urls };
    });
  };

  const handleAddWatti = () => {
    const f = parseFloat(wItem.fromGrams),
      t = parseFloat(wItem.toGrams),
      q = parseInt(wItem.quantity, 10);
    if (isNaN(f) || isNaN(t) || isNaN(q) || f >= t || q <= 0)
      return alert("Invalid inputs.");
    setWattiItems([...wattiItems, { from: f, to: t, quantity: q }]);
    setWItem({ fromGrams: "", toGrams: "", quantity: "" });
  };

  const calculateTotalItems = () => {
    if (orderType === "Single")
      return rows.reduce((s, r) => s + (parseInt(r.quantity) || 0), 0);
    if (orderType === "Bulk") {
      if (subType === "Mangalsutra") {
        let t = 0;
        BEAD_SIZES.forEach((s) =>
          LENGTHS.forEach((l) => (t += parseInt(matrix[s][l] || 0))),
        );
        return t;
      }
      return wattiItems.reduce((s, i) => s + i.quantity, 0);
    }
    if (orderType === "Casting") return 1;
    return 0;
  };

  const calculateEstWeight = () => {
    if (orderType === "Single")
      return rows.reduce(
        (s, r) =>
          s +
          ((parseFloat(r.weight) || 0) + (parseFloat(r.pendantWeight) || 0)) *
            (parseInt(r.quantity) || 1),
        0,
      );
    return 0;
  };

  // --- SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.deliveryDate) return alert("Delivery date is required.");
    setIsSubmitting(true);

    try {
      const orderId = await generateOrderId(new Date());
      let docData = {
        orderId,
        partyId: selectedPartyId,
        orderType,
        deliveryDate: form.deliveryDate,
        comments: form.comments,
        status: "Pending",
        createdAt: serverTimestamp(),
        files: [],
      };

      if (orderType === "Single") {
        docData.product = form.product;
        docData.totalQuantity = calculateTotalItems();
        docData.singleItems = rows.map((r) => {
          const { rawFiles, previewUrls, id, ...clean } = r;
          clean.fileUrls = [];
          clean.itemStatus = "Pending";
          return clean;
        });
      } else if (orderType === "Bulk") {
        docData.subType = subType;
        docData.items = [];
        if (subType === "Mangalsutra") {
          BEAD_SIZES.forEach((s) =>
            LENGTHS.forEach((l) => {
              if (parseInt(matrix[s][l]) > 0)
                docData.items.push({
                  beadSize: s,
                  length: `${l} inch`,
                  quantity: parseInt(matrix[s][l]),
                });
            }),
          );
          if (!docData.items.length)
            throw new Error("Enter at least one matrix quantity.");
        } else {
          if (!wattiItems.length)
            throw new Error("Add at least one Watti range.");
          docData.items = wattiItems.map((i) => ({
            type: "Watti Mani",
            weightRange: `${i.from}g-${i.to}g`,
            quantity: i.quantity,
          }));
        }
      } else if (orderType === "Casting") {
        docData.castingDetails = {
          item:
            castingDetails.item === "Custom"
              ? castingDetails.customItem
              : castingDetails.item,
          carat:
            castingDetails.carat === "Custom"
              ? castingDetails.customCarat
              : castingDetails.carat,
          weight: castingDetails.weight,
          size: castingDetails.size,
          sizeUnit: castingDetails.sizeUnit,
          stone:
            castingDetails.stone === "Custom"
              ? castingDetails.customStone
              : castingDetails.stone,
          budget: castingDetails.budget,
          finalWeight: "",
        };
      }

      const docRef = await addDoc(collection(db, "partyOrders"), docData);
      let needsUpdate = false;

      // PARALLEL IMAGE UPLOADS
      if (orderType === "Single") {
        await Promise.all(
          rows.map(async (row, i) => {
            if (row.rawFiles?.length > 0) {
              needsUpdate = true;
              const uploadPromises = row.rawFiles.map(async (file, j) => {
                const sRef = ref(
                  storage,
                  `orders_images/${docRef.id}/row${i}_img${j}_${Date.now()}_${file.name}`,
                );
                const snap = await uploadBytes(sRef, file);
                return await getDownloadURL(snap.ref);
              });
              docData.singleItems[i].fileUrls =
                await Promise.all(uploadPromises);
            }
          }),
        );
      } else {
        if (globalFiles.rawFiles.length > 0) {
          needsUpdate = true;
          const uploadPromises = globalFiles.rawFiles.map(async (file, j) => {
            const sRef = ref(
              storage,
              `orders_images/${docRef.id}/global_img${j}_${Date.now()}_${file.name}`,
            );
            const snap = await uploadBytes(sRef, file);
            return await getDownloadURL(snap.ref);
          });
          docData.files = await Promise.all(uploadPromises);
        }
      }

      if (needsUpdate)
        await updateDoc(
          docRef,
          orderType === "Single"
            ? { singleItems: docData.singleItems }
            : { files: docData.files },
        );

      setSuccessOrder({ ...docData, id: docRef.id });

      // ==========================================
      // RESET FORM STATE AFTER SUCCESSFUL SUBMISSION
      // ==========================================
      setRows([
        {
          id: Date.now(),
          rawFiles: [],
          previewUrls: [],
          length: "",
          lengthUnit: "Inches",
          weight: "",
          pendantLength: "",
          pendantLengthUnit: "Inches",
          pendantWeight: "",
          quantity: "1",
        },
      ]);

      setForm({ product: products[0], deliveryDate: "", comments: "" });
      setGlobalFiles({ rawFiles: [], previewUrls: [] });

      // Reset Bulk State
      setWattiItems([]);
      setWItem({ fromGrams: "", toGrams: "", quantity: "" });
      setMatrix(() => {
        let init = {};
        BEAD_SIZES.forEach((s) => {
          init[s] = {};
          LENGTHS.forEach((l) => (init[s][l] = ""));
        });
        return init;
      });

      // Reset Casting State
      setCastingDetails({
        item: "Ring",
        customItem: "",
        carat: "22K",
        customCarat: "",
        weight: "",
        size: "",
        sizeUnit: "Ring Size",
        stone: "Diamond",
        customStone: "",
        budget: "",
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const partyName =
    parties.find((p) => p.id === selectedPartyId)?.name || "Unknown Party";

  return (
    <div className="erp-create-order-page">
      {isPartyModalOpen && (
        <AddPartyModal
          onClose={() => setIsPartyModalOpen(false)}
          onSuccess={(newParty) => {
            setParties([...parties, newParty]);
            setSelectedPartyId(newParty.id);
            setOrderType("Single");
          }}
        />
      )}
      {isProductModalOpen && (
        <AddProductModal
          onClose={() => setIsProductModalOpen(false)}
          onSuccess={(newProd) => {
            if (!products.includes(newProd))
              setProducts([...products, newProd]);
            setForm({ ...form, product: newProd });
          }}
        />
      )}

      {isSubmitting && (
        <div className="erp-loader-overlay erp-fade-in">
          <div className="erp-spinner"></div>
          <h2>Securing Order</h2>
          <p>Processing payload into ERP...</p>
        </div>
      )}

      {/* FULLY STYLED SUCCESS MODAL (PORTAL) */}
      {successOrder &&
        createPortal(
          <div
            className="erp-modal-backdrop erp-fade-in"
            style={{ zIndex: 99999 }}
          >
            <div className="erp-success-modal-card erp-pop-in">
              <div className="success-icon-wrapper">
                <FaCheckCircle className="check-icon" />
              </div>
              <h2 className="success-title">Order Logged Successfully!</h2>

              <div className="order-id-highlight">
                <span className="label">TICKET ID</span>
                <span className="value">#{successOrder.orderId}</span>
              </div>

              <div className="action-buttons-stack">
                <button
                  className="btn-whatsapp"
                  onClick={() => openWhatsApp(successOrder, partyName)}
                >
                  <FaWhatsapp /> Share to WhatsApp
                </button>
                <button
                  className="btn-print"
                  onClick={() => triggerPrintJobCard(successOrder, partyName)}
                >
                  <FaPrint /> Print Job Card
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setSuccessOrder(null)}
                >
                  Continue Creating
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <div className="erp-page-header erp-fade-up">
        <div className="header-left">
          <h1>Order Entry Gateway</h1>
          <p>Unified interface for Single, Bulk, and Casting operations.</p>
        </div>
      </div>

      <div className="erp-top-controls-card erp-fade-up">
        <div
          className="erp-input-group"
          style={{ maxWidth: "400px", margin: 0 }}
        >
          <label>Select Master Account / Party</label>
          <select
            value={selectedPartyId}
            onChange={(e) => {
              if (e.target.value === "ADD_NEW") setIsPartyModalOpen(true);
              else {
                setSelectedPartyId(e.target.value);
                setOrderType("Single");
              }
            }}
          >
            <option value="" disabled>
              -- Authenticate Party --
            </option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            <option value="ADD_NEW" className="add-new-option">
              ➕ Add New Party...
            </option>
          </select>
        </div>

        {selectedPartyId && (
          <div className="erp-order-type-tabs">
            <button
              className={orderType === "Single" ? "active" : ""}
              onClick={() => setOrderType("Single")}
            >
              Single Items
            </button>
            <button
              className={orderType === "Bulk" ? "active" : ""}
              onClick={() => setOrderType("Bulk")}
            >
              Bulk Matrix
            </button>
            <button
              className={orderType === "Casting" ? "active" : ""}
              onClick={() => setOrderType("Casting")}
            >
              Casting Orders
            </button>
          </div>
        )}
      </div>

      {selectedPartyId && orderType && (
        <form
          className="erp-order-form erp-fade-in"
          onSubmit={handleSubmit}
          id="main-order-form"
        >
          <div className="form-header">
            <h3>⚙️ {orderType} Specification</h3>
          </div>

          <div className="erp-form-grid-2">
            {orderType === "Single" && (
              <div className="erp-input-group">
                <label>Product Class</label>
                <select
                  value={form.product}
                  onChange={(e) => {
                    if (e.target.value === "ADD_NEW")
                      setIsProductModalOpen(true);
                    else setForm({ ...form, product: e.target.value });
                  }}
                >
                  {products.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                  <option value="ADD_NEW" className="add-new-option">
                    ➕ Add New Product...
                  </option>
                </select>
              </div>
            )}
            <div className="erp-input-group">
              <label>Committed Delivery Date</label>
              <input
                type="date"
                value={form.deliveryDate}
                onChange={(e) =>
                  setForm({ ...form, deliveryDate: e.target.value })
                }
                required
              />
            </div>
          </div>

          {/* SINGLE TYPE */}
          {orderType === "Single" && (
            <div className="erp-dynamic-rows-container">
              {rows.map((row, index) => {
                const isMSAndPendant = form.product === "Mangalsutra + Pendant";
                return (
                  <div key={row.id} className="erp-item-row-card">
                    <div className="row-photo-section">
                      <label
                        className="photo-upload-box"
                        htmlFor={`photo-${row.id}`}
                      >
                        <FaImage className="icon" />
                        <span>Add Photos</span>
                      </label>
                      <input
                        type="file"
                        id={`photo-${row.id}`}
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          if (!files.length) return;
                          const urls = files.map((f) => URL.createObjectURL(f));
                          const u = [...rows];
                          u[index].rawFiles = [...u[index].rawFiles, ...files];
                          u[index].previewUrls = [
                            ...u[index].previewUrls,
                            ...urls,
                          ];
                          setRows(u);
                        }}
                      />
                      <div className="erp-image-gallery">
                        {row.previewUrls.map((url, imgIdx) => (
                          <div key={imgIdx} className="img-wrapper erp-pop-in">
                            <img src={url} alt={`Preview`} />
                            <button
                              type="button"
                              className="btn-remove-img"
                              onClick={() => {
                                const u = [...rows];
                                u[index].rawFiles.splice(imgIdx, 1);
                                u[index].previewUrls.splice(imgIdx, 1);
                                setRows(u);
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="row-measurements-section">
                      <div className="measurement-group">
                        <span className="group-title">Primary Specs</span>
                        <div className="inputs-inline">
                          <input
                            type="text"
                            placeholder="Weight (e.g. 30g)"
                            value={row.weight}
                            onChange={(e) => {
                              const u = [...rows];
                              u[index].weight = e.target.value;
                              setRows(u);
                            }}
                            required
                          />
                          <input
                            type="text"
                            placeholder="Length/Size"
                            value={row.length}
                            onChange={(e) => {
                              const u = [...rows];
                              u[index].length = e.target.value;
                              setRows(u);
                            }}
                            required
                          />
                          <select
                            value={row.lengthUnit}
                            onChange={(e) => {
                              const u = [...rows];
                              u[index].lengthUnit = e.target.value;
                              setRows(u);
                            }}
                          >
                            <option>Inches</option>
                            <option>MM</option>
                            <option>Gala</option>
                            <option>Size</option>
                          </select>
                        </div>
                      </div>
                      {isMSAndPendant && (
                        <div className="measurement-group">
                          <span className="group-title">Pendant Specs</span>
                          <div className="inputs-inline">
                            <input
                              type="text"
                              placeholder="Pendant Wt"
                              value={row.pendantWeight}
                              onChange={(e) => {
                                const u = [...rows];
                                u[index].pendantWeight = e.target.value;
                                setRows(u);
                              }}
                              required
                            />
                            <input
                              type="text"
                              placeholder="Pendant Size"
                              value={row.pendantLength}
                              onChange={(e) => {
                                const u = [...rows];
                                u[index].pendantLength = e.target.value;
                                setRows(u);
                              }}
                            />
                            <select
                              value={row.pendantLengthUnit}
                              onChange={(e) => {
                                const u = [...rows];
                                u[index].pendantLengthUnit = e.target.value;
                                setRows(u);
                              }}
                            >
                              <option>Inches</option>
                              <option>MM</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="row-actions-section">
                      <div className="erp-input-group qty-box">
                        <label>Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={row.quantity}
                          onChange={(e) => {
                            const u = [...rows];
                            u[index].quantity = e.target.value;
                            setRows(u);
                          }}
                          required
                        />
                      </div>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-row"
                          onClick={() =>
                            setRows(rows.filter((_, i) => i !== index))
                          }
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn-add-row"
                onClick={() =>
                  setRows([
                    ...rows,
                    {
                      id: Date.now(),
                      rawFiles: [],
                      previewUrls: [],
                      length: "",
                      lengthUnit: "Inches",
                      weight: "",
                      pendantLength: "",
                      pendantLengthUnit: "Inches",
                      pendantWeight: "",
                      quantity: "1",
                    },
                  ])
                }
              >
                <FaPlus /> Add Variation (Alt+A)
              </button>
            </div>
          )}

          {/* BULK TYPE */}
          {orderType === "Bulk" && (
            <div className="erp-fade-in" style={{ marginTop: "24px" }}>
              <div
                className="erp-order-type-tabs"
                style={{ marginBottom: "20px", marginTop: 0 }}
              >
                <button
                  type="button"
                  className={subType === "Mangalsutra" ? "active" : ""}
                  onClick={() => setSubType("Mangalsutra")}
                >
                  Matrix Grid
                </button>
                <button
                  type="button"
                  className={subType === "Watti mani" ? "active" : ""}
                  onClick={() => setSubType("Watti mani")}
                >
                  Watti Ranges
                </button>
              </div>

              {subType === "Mangalsutra" ? (
                <div className="erp-table-responsive">
                  <table className="erp-bulk-matrix-table">
                    <thead>
                      <tr>
                        <th>Bead \\ Len</th>
                        {LENGTHS.map((l) => (
                          <th key={l}>{l}"</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {BEAD_SIZES.map((size) => (
                        <tr key={size}>
                          <td className="row-label">{size}</td>
                          {LENGTHS.map((len) => (
                            <td key={len} className="input-cell">
                              <input
                                type="number"
                                min="0"
                                value={matrix[size][len]}
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                  setMatrix((p) => ({
                                    ...p,
                                    [size]: { ...p[size], [len]: val },
                                  }));
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="erp-watti-container">
                  <div className="erp-form-grid-3">
                    <div className="erp-input-group">
                      <label>From (g)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={wItem.fromGrams}
                        onChange={(e) =>
                          setWItem({ ...wItem, fromGrams: e.target.value })
                        }
                      />
                    </div>
                    <div className="erp-input-group">
                      <label>To (g)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={wItem.toGrams}
                        onChange={(e) =>
                          setWItem({ ...wItem, toGrams: e.target.value })
                        }
                      />
                    </div>
                    <div className="erp-input-group">
                      <label>Qty</label>
                      <input
                        type="number"
                        value={wItem.quantity}
                        onChange={(e) =>
                          setWItem({ ...wItem, quantity: e.target.value })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ height: "44px", alignSelf: "flex-end" }}
                      onClick={handleAddWatti}
                    >
                      <FaPlus /> Add
                    </button>
                  </div>
                  {wattiItems.length > 0 && (
                    <table className="erp-watti-table">
                      <thead>
                        <tr>
                          <th>Weight Range</th>
                          <th>Quantity</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wattiItems.map((item, i) => (
                          <tr key={i}>
                            <td>
                              {item.from}g - {item.to}g
                            </td>
                            <td className="highlight-qty">{item.quantity}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-table-remove"
                                onClick={() =>
                                  setWattiItems((p) =>
                                    p.filter((_, idx) => idx !== i),
                                  )
                                }
                              >
                                <FaTrash /> Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* CASTING TYPE */}
          {orderType === "Casting" && (
            <div className="erp-casting-container erp-fade-in">
              <div className="erp-form-grid-3">
                <div className="erp-input-group">
                  <label>Item</label>
                  <select
                    value={castingDetails.item}
                    onChange={(e) => {
                      const val = e.target.value;
                      let defaultUnit = "Standard";
                      if (val === "Ring") defaultUnit = "Ring Size";
                      else if (val === "Bangle") defaultUnit = "Gala";
                      else if (val === "Pendant" || val === "Earrings")
                        defaultUnit = "MM";

                      setCastingDetails({
                        ...castingDetails,
                        item: val,
                        sizeUnit: defaultUnit,
                      });
                    }}
                  >
                    <option>Ring</option>
                    <option>Bangle</option>
                    <option>Pendant</option>
                    <option>Earrings</option>
                    <option>Custom</option>
                  </select>
                  {castingDetails.item === "Custom" && (
                    <input
                      style={{ marginTop: "10px" }}
                      placeholder="Specify"
                      value={castingDetails.customItem}
                      onChange={(e) =>
                        setCastingDetails({
                          ...castingDetails,
                          customItem: e.target.value,
                        })
                      }
                      required
                    />
                  )}
                </div>

                <div className="erp-input-group">
                  <label>Carat</label>
                  <select
                    value={castingDetails.carat}
                    onChange={(e) =>
                      setCastingDetails({
                        ...castingDetails,
                        carat: e.target.value,
                      })
                    }
                  >
                    <option>22K</option>
                    <option>18K</option>
                    <option>14K</option>
                    <option>Custom</option>
                  </select>
                  {castingDetails.carat === "Custom" && (
                    <input
                      style={{ marginTop: "10px" }}
                      placeholder="Specify"
                      value={castingDetails.customCarat}
                      onChange={(e) =>
                        setCastingDetails({
                          ...castingDetails,
                          customCarat: e.target.value,
                        })
                      }
                      required
                    />
                  )}
                </div>

                <div className="erp-input-group">
                  <label>Est. Wt</label>
                  <input
                    placeholder="e.g. 5g"
                    value={castingDetails.weight}
                    onChange={(e) =>
                      setCastingDetails({
                        ...castingDetails,
                        weight: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                {/* NEW: Contextual Sizing Field */}
                <div className="erp-input-group">
                  <label>Size / Measurement</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      style={{ flex: 1 }}
                      placeholder={
                        castingDetails.sizeUnit === "Gala"
                          ? "e.g. 2.4"
                          : "e.g. 16"
                      }
                      value={castingDetails.size}
                      onChange={(e) =>
                        setCastingDetails({
                          ...castingDetails,
                          size: e.target.value,
                        })
                      }
                      required
                    />
                    <select
                      value={castingDetails.sizeUnit}
                      onChange={(e) =>
                        setCastingDetails({
                          ...castingDetails,
                          sizeUnit: e.target.value,
                        })
                      }
                      style={{ flex: "0 0 105px", padding: "0 8px" }}
                    >
                      <option value="Ring Size">Ring Size</option>
                      <option value="Gala">Gala</option>
                      <option value="MM">MM</option>
                      <option value="Inches">Inches</option>
                      <option value="Standard">Standard</option>
                    </select>
                  </div>
                </div>

                <div className="erp-input-group">
                  <label>Stone</label>
                  <select
                    value={castingDetails.stone}
                    onChange={(e) =>
                      setCastingDetails({
                        ...castingDetails,
                        stone: e.target.value,
                      })
                    }
                  >
                    <option>Diamond</option>
                    <option>AD</option>
                    <option>None</option>
                    <option>Custom</option>
                  </select>
                  {castingDetails.stone === "Custom" && (
                    <input
                      style={{ marginTop: "10px" }}
                      placeholder="Specify"
                      value={castingDetails.customStone}
                      onChange={(e) =>
                        setCastingDetails({
                          ...castingDetails,
                          customStone: e.target.value,
                        })
                      }
                      required
                    />
                  )}
                </div>

                <div className="erp-input-group">
                  <label>Budget</label>
                  <input
                    placeholder="e.g. ₹50,000"
                    value={castingDetails.budget}
                    onChange={(e) =>
                      setCastingDetails({
                        ...castingDetails,
                        budget: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* GLOBAL FILES FOR BULK / CASTING */}
          {(orderType === "Bulk" || orderType === "Casting") && (
            <div className="erp-global-files-container erp-fade-in">
              <div className="upload-section">
                <label className="global-photo-upload-box">
                  <FaPlus className="icon-plus" />
                  <span>Global Refs</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleGlobalFiles}
                  />
                </label>
              </div>
              <div className="erp-image-gallery">
                {globalFiles.previewUrls.map((url, i) => (
                  <div key={i} className="img-wrapper erp-pop-in">
                    <img src={url} alt="Ref" />
                    <button
                      type="button"
                      className="btn-remove-img"
                      onClick={() => removeGlobalImage(i)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MASTER INSTRUCTIONS */}
          <div className="erp-input-group" style={{ marginTop: "24px" }}>
            <label>Master Instructions</label>
            <textarea
              rows="2"
              value={form.comments}
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
              placeholder="ERP level routing notes..."
            />
          </div>
        </form>
      )}

      {/* STICKY SUMMARY BAR */}
      {selectedPartyId && orderType && (
        <div className="erp-sticky-summary erp-fade-up">
          <div className="keyboard-hint">
            Use <strong>Ctrl + Enter</strong> to execute order.
          </div>
          {orderType === "Single" && (
            <div className="stat-block">
              <span className="lbl">Est. Gross Wt</span>
              <span className="val highlight-gold">
                {calculateEstWeight()}g
              </span>
            </div>
          )}
          <div className="stat-block">
            <span className="lbl">Total Items</span>
            <span className="val highlight-blue">{calculateTotalItems()}</span>
          </div>
          <button
            type="submit"
            form="main-order-form"
            className="btn-submit"
            id="main-submit-btn"
          >
            Execute Order Ticket
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateOrder;
